import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { SECRET_VALUE_MAX, SecretService } from '../../services/secret.service';

/**
 * The plaintext lives only in the password field and is dropped on close; whoever opens the
 * dialog removes it from the DOM on `closed`.
 */
@Component({
  selector: 'app-secret-encrypt-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatFormField, MatInput, MatLabel, MatProgressSpinner, Modal, TranslatePipe],
  templateUrl: './secret-encrypt-dialog.component.html',
  styleUrl: './secret-encrypt-dialog.component.scss',
})
export class SecretEncryptDialogComponent {
  private readonly secretService = inject(SecretService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();

  readonly inserted = output<string>();
  readonly closed = output<void>();

  readonly maxLength = SECRET_VALUE_MAX;

  readonly password = signal('');
  readonly encrypted = signal('');
  readonly loading = signal(false);
  readonly failed = signal(false);

  readonly canEncrypt = computed(() => this.password().length > 0 && !this.loading());

  onPasswordInput(value: string): void {
    this.password.set(value);
    this.encrypted.set('');
  }

  encrypt(): void {
    if (!this.canEncrypt()) return;
    this.loading.set(true);
    this.failed.set(false);
    this.secretService
      .encrypt(this.projectId(), this.password())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value) => {
          this.loading.set(false);
          this.encrypted.set(value);
        },
        error: () => {
          this.loading.set(false);
          this.failed.set(true);
        },
      });
  }

  copy(): void {
    const value = this.encrypted();
    if (!value) return;
    // Without clipboard access the value stays visible in the result field for manual copying.
    navigator.clipboard?.writeText(value).then(
      () => this.snackBar.open(this.translate.instant('secretEncrypt.copied'), undefined, { duration: 2000 }),
      () => undefined,
    );
  }

  insert(): void {
    const value = this.encrypted();
    if (!value) return;
    this.inserted.emit(value);
    this.close();
  }

  close(): void {
    this.password.set('');
    this.encrypted.set('');
    this.closed.emit();
  }
}
